import React, { useEffect, useState } from "react";
import {
  AppBar,
  Typography,
  Toolbar,
  Button,
  Modal,
  Box,
  useTheme,
  useMediaQuery,
  Tooltip,
  Drawer,
  Divider,
} from "@mui/material";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import IconButton from "@mui/material/IconButton";
import { useAuth } from "../hooks/AuthProvider";
import { Menu, SwapHoriz } from "@mui/icons-material";
import {
  GET_DASHBOARD_WABA_STATUS,
  GET_RCS_BRAND,
  GET_SUBSCRIPTIONS,
  SETTINGS_DASHBOARD_ENDPOINT,
  USERS_ENDPOINT,
  WA_BUSINESS_ACCOUNT_DATA,
  WA_BUSINESS_ACCOUNT_PHOTO,
} from "../config/urls.js";
import axios from "axios";
import BusinessProfileCard from "./Header/business-profile-card.jsx";
import { useNavigate } from "react-router-dom";
import StatusCard from "./Header/status-card.jsx";
import WhatsappButton from "./Header/whatsapp-button.jsx";
import ProfileDetailsCard from "./Header/profile-details-card.jsx";

const Header = ({
  userName,
  userState,
  drawerOpen = false,
  setDrawerOpen = null,
}) => {
  const { getValidToken } = useAuth();
  const auth = useAuth();
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const [addons, setAddons] = useState([]);
  const [brands, setBrands] = useState();
  const theme = useTheme();
  
  // Check for RCS in two ways:
  // 1. From addons (subscription data)
  // 2. From apps/modules list in localStorage (more reliable)
  const [isRCS, setIsRCS] = useState(false);
  const isSmallScreen = useMediaQuery("(max-width:1300px)");
  const [headerDrawerOpen, setHeaderDrawerOpen] = useState(false);
  

  // Same channel markers as the WA/RCS page tabs: conversations.view is granted only by WA addons
  const permissionsFromStorage = JSON.parse(localStorage.getItem("permissions") || "[]");
  const hasRcs = permissionsFromStorage.includes("rcs_bot_create.view");
  const hasWa = permissionsFromStorage.includes("conversations.view") || !hasRcs;

  const [controller, setController] = useState({
    page: 0,
    rowsPerPage: 20,
  });

  const [Loading, setLoading] = useState({
    business: false,
    profile: false,
  });

  const [userProfileData, setUserProfileData] = useState({
    name: "",
    email: "",
    photo: "",
    plan: "",
    userCount: "",
    businessName: "",
    phone: "",
    whatsappBusinessApiStatus: "disconnected",
  });

  const tenant = localStorage.getItem("tenant");

  const formatPlanName = (plan) => {
    if (!plan) return "No plan selected";
    const formatted = plan.replace(/_/g, " ");
    return formatted.charAt(0).toUpperCase() + formatted.slice(1).toLowerCase();
  };
  const navigate = useNavigate();

  useEffect(() => {
    fetchWabaDetails();
    fetchSettingsData();
    fetchProfilePhoto();
    fetchData();
    fetchBusinessData();
    loadCurrentSubscription();
    setUserProfileData((prev) => ({
      ...prev,
      plan: formatPlanName(localStorage.getItem("plan")),
    }));
  }, []);

  const fetchSettingsData = async () => {
    try {
      const token = await getValidToken();
      const headers = {
        Authorization: `Bearer ${token}`,
        "X-Tenant-ID": tenant,
      };

      const response = await axios.get(SETTINGS_DASHBOARD_ENDPOINT, {
        headers,
      });

      const data = response?.data;

      setUserProfileData((prev) => ({
        ...prev,
        name: data?.user_name,
        email: data?.user_email,
        phone: data?.business_wa_number,
      }));
    } catch (error) {
      console.error(error);
    }
  };

  const fetchBusinessData = async () => {
    try {
      setLoading((prev) => ({ ...prev, business: true }));

      const response = await axios.get(WA_BUSINESS_ACCOUNT_DATA, {
        headers: { "X-Tenant-ID": tenant },
      });

      if (response?.data?.business) {
        setUserProfileData((prev) => ({
          ...prev,
          businessName: response.data.business.name || "",
        }));
      }
    } finally {
      setLoading((prev) => ({ ...prev, business: false }));
    }
  };

  const fetchProfilePhoto = async () => {
    try {
      const response = await axios.get(WA_BUSINESS_ACCOUNT_PHOTO, {
        headers: { "X-Tenant-ID": tenant },
      });

      setUserProfileData((prev) => ({
        ...prev,
        photo: response?.data?.message,
      }));
    } catch (error) {
      console.error(error);
    }
  };

  const fetchData = async () => {
    try {
      const token = await getValidToken();

      const response = await fetch(
        USERS_ENDPOINT +
          `?limit=${controller.rowsPerPage}&offset=${
            controller.page * controller.rowsPerPage
          }`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Tenant-Id": tenant,
          },
        },
      );

      const jsonData = await response.json();

      setUserProfileData((prev) => ({
        ...prev,
        userCount: jsonData?.count,
      }));
    } catch (error) {
      console.error(error);
    }
  };

  const fetchWabaDetails = async () => {
    try {
      const response = await axios.get(GET_DASHBOARD_WABA_STATUS, {
        headers: { "X-Tenant-ID": tenant },
      });

      setUserProfileData((prev) => ({
        ...prev,
        whatsappBusinessApiStatus:
          response?.data?.wabaInfo?.dockerStatus || "N/A",
      }));
    } catch (error) {
      console.error(error);
    }
  };

  const loadCurrentSubscription = async () => {
    const token = await getValidToken();

    try {
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Tenant-Id": tenant,
      };

      const subscriptionResponse = await fetch(GET_SUBSCRIPTIONS, { headers });
      const data = await subscriptionResponse.json();
      setAddons(data?.addons || []);
    } catch (err) {
      console.error("Error loading subscription:", err);
    }
  };

  const multiple_tenants = localStorage.getItem("multiple_tenants") || false;

  if (userState === "Idle") {
    auth.logOut();
  }

  const handleOpenDrawer = () => {
    if (setDrawerOpen !== null) {
      setDrawerOpen(!drawerOpen);
    }
  };

  const handleLogout = () => {
    auth.logOut();
    setLogoutModalOpen(false);
  };

  const handleSwitchTenant = () => {
    auth.redirectToSwitchTenant();
  };

  const toggleHeaderDrawer = () => {
    setHeaderDrawerOpen((prev) => !prev);
  };

  return (
    <>
      <AppBar
        position="relative"
        sx={{
          boxShadow: "none",
          borderBottom: "1px solid #E1E8F2",
          height: "auto",
        }}
      >
        <Toolbar
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            height: "auto",
          }}
        >
          {/* 🔥 Hamburger at <1300px */}

          <BusinessProfileCard userProfileData={userProfileData} />
          {isSmallScreen && (
            <IconButton onClick={toggleHeaderDrawer}>
              <Menu />
            </IconButton>
          )}
          {/* 🔥 Desktop only */}
          {!isSmallScreen && (
            <Box
              sx={{
                marginLeft: "auto",
                display: "flex",
                alignItems: "center",
                gap: "16px",
              }}
            >
              {hasWa && (
                <>
                  <StatusCard userProfileData={userProfileData} isRcs={false} />
                  <WhatsappButton isRcs={false} />
                </>
              )}
              {hasRcs && (
                <>
                  <StatusCard userProfileData={userProfileData} isRcs />
                  <WhatsappButton isRcs />
                </>
              )}
              <ProfileDetailsCard userProfileData={userProfileData} />

              {multiple_tenants && (
                <Tooltip title="Switch Tenant">
                  <IconButton onClick={handleSwitchTenant}>
                    <SwapHoriz />
                  </IconButton>
                </Tooltip>
              )}

              <Tooltip title="Logout">
                <IconButton onClick={() => setLogoutModalOpen(true)}>
                  <PowerSettingsNewIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* 🔥 DRAWER */}
      <Drawer
        anchor="right"
        open={headerDrawerOpen}
        onClose={toggleHeaderDrawer}
      >
        <Box sx={{ width: 400, mx: 0, py: 2, marginTop: "64px" }}>
          {hasWa && (
            <>
              <Box sx={{ mx: 2, display: "flex", alignItems: "center", gap: 2 }}>
                <WhatsappButton isRcs={false} />
                <StatusCard userProfileData={userProfileData} isRcs={false} />
              </Box>
              <Divider sx={{ my: 2 }} />
            </>
          )}
          {hasRcs && (
            <>
              <Box sx={{ mx: 2, display: "flex", alignItems: "center", gap: 2 }}>
                <WhatsappButton isRcs />
                <StatusCard userProfileData={userProfileData} isRcs />
              </Box>
              <Divider sx={{ my: 2 }} />
            </>
          )}
          <Box sx={{ mx: 2 }}>
            <ProfileDetailsCard userProfileData={userProfileData} />
          </Box>
          <Divider sx={{ my: 2 }} />

          {multiple_tenants && (
            <>
              <Box sx={{ mx: 2 }}>
                <Button onClick={handleSwitchTenant} fullWidth>
                  Switch Tenant
                </Button>
              </Box>
              <Divider sx={{ my: 2 }} />
            </>
          )}

          <Button
            onClick={() => setLogoutModalOpen(true)}
            color="error"
            fullWidth
          >
            Logout
          </Button>
        </Box>
      </Drawer>

      {/* MODAL */}
      <Modal open={logoutModalOpen} onClose={() => setLogoutModalOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            p: 4,
            maxWidth: 400,
            width: "80%",
            textAlign: "center",
          }}
        >
          <Typography variant="h6">Are you sure you want to logout?</Typography>

          <Box sx={{ display: "flex", justifyContent: "space-around", mt: 2 }}>
            <Button onClick={handleLogout} variant="contained" color="error">
              Logout
            </Button>
            <Button
              onClick={() => setLogoutModalOpen(false)}
              variant="contained"
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default React.memo(Header);
